import { Link } from '@tanstack/react-router';
import { LogOut, Plus, X } from 'lucide-react';
import { useId, useRef, useState, type RefObject } from 'react';
import { useModalFocus } from '../../lib/modal-focus.ts';
import { useSession } from '../../lib/session.tsx';
import { attemptLeave } from '../../lib/unsaved.tsx';
import { ThemeRow } from '../profile-menu.tsx';
import { NewMenuList } from '../s3-header.tsx';
import { Avatar, LogoMark, useCounterOf, useRoleNav, Wordmark } from './nav-parts.tsx';
import { WideLink } from './sidebar.tsx';

/**
 * Menü-Schublade am Handy im S3-Stil (Change 11.10.2026 Design S3, Story 1.77; DESIGN „Menü-Schublade“, EXPERIENCE
 * §4.1, §10): von links, weiß, Radius der Symbolleiste, Liste dahinter abgedunkelt. Bereiche mit Unterpunkten stehen als
 * kleine Überschrift über ihren Einträgen (alle offen – am Handy kein Überfahren). Fokus beim Öffnen auf den aktiven
 * Eintrag, bleibt in der Schublade; Esc, ✕, Tipp daneben, Wischen nach links oder ein Eintrag schließen, danach Fokus
 * auf den Menü-Knopf. Oben steht „Neu anlegen“ (Story 1.114, [RUDOLF 13.10.2026]): Hauptknopf zum Aufklappen mit denselben
 * Anlege-Zielen wie „+ Neu“ am Desktop; am Handy gibt es kein „+“ mehr in der Kopfzeile.
 */
export function NavDrawer({ id, opener, onClose }: { id: string; opener: RefObject<HTMLButtonElement | null>; onClose: () => void }) {
  const { me, auth } = useSession();
  const counterOf = useCounterOf();
  const { areas, foot, newMenu } = useRoleNav();
  const panel = useRef<HTMLDivElement>(null);
  const touchX = useRef<number | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const newListId = useId();
  useModalFocus(panel, opener, onClose, () => panel.current?.querySelector<HTMLElement>('nav a[data-status="active"]') ?? null);
  return (
    <>
      <div data-testid="menue-abdunklung" aria-hidden className="fixed inset-0 z-40 bg-scrim" onClick={onClose} />
      <div
        id={id}
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Menü"
        tabIndex={-1}
        onTouchStart={(e) => {
          touchX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          const start = touchX.current;
          const end = e.changedTouches[0]?.clientX;
          touchX.current = null;
          if (start !== null && end !== undefined && start - end > 60) onClose();
        }}
        className="fixed inset-y-0 left-0 z-50 flex w-[min(320px,85vw)] animate-[drawer-in_200ms_ease-out] flex-col overflow-y-auto rounded-r-rail bg-card px-3 pt-4 pb-4 shadow-flyout motion-reduce:animate-none"
      >
        <div className="flex items-center gap-[10px] pb-4 pl-1">
          <LogoMark />
          <Wordmark />
          <button
            type="button"
            onClick={onClose}
            aria-label="Menü schließen"
            className="ml-auto grid size-11 cursor-pointer place-items-center rounded-full text-ink-2"
          >
            <X size={20} strokeWidth={1.5} aria-hidden />
          </button>
        </div>
        {newMenu.length > 0 ? (
        <div className="mb-2 flex flex-col gap-1 pointer-coarse:gap-2">
          <button
            type="button"
            onClick={() => setNewOpen((v) => !v)}
            aria-expanded={newOpen}
            aria-controls={newListId}
            className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-full bg-brand px-4 text-row whitespace-nowrap text-on-brand hover:bg-brand-hover"
          >
            <Plus size={18} strokeWidth={1.75} aria-hidden />
            Neu anlegen
          </button>
          <div id={newListId} hidden={!newOpen}>
            <NewMenuList onNavigate={onClose} large items={newMenu} />
          </div>
        </div>
        ) : null}
        <nav aria-label="Hauptnavigation" className="flex flex-col gap-1 pointer-coarse:gap-2">
          {areas.map((area) =>
            area.items.length === 1 ? (
              <WideLink key={area.label} item={area.items[0]!} counter={counterOf(area.items[0]!)} onNavigate={onClose} large />
            ) : (
              <div key={area.label} role="group" aria-label={area.label} className="mt-2 flex flex-col gap-1 pointer-coarse:gap-2">
                <span aria-hidden className="px-3 pb-[2px] text-caption text-ink-3">
                  {area.label}
                </span>
                {area.items.map((item) => (
                  <WideLink key={item.path} item={item} counter={counterOf(item)} onNavigate={onClose} large />
                ))}
              </div>
            ),
          )}
        </nav>
        <div className="mt-4 flex flex-col gap-1 border-t border-line pt-3 pointer-coarse:gap-2">
          {foot.length > 0 ? (
            <nav aria-label="Verwaltung" className="flex flex-col gap-1 pointer-coarse:gap-2">
              {foot.map((item) => (
                <WideLink key={item.path} item={item} onNavigate={onClose} large />
              ))}
            </nav>
          ) : null}
          <ThemeRow />
          <div className="flex items-center gap-2" aria-label="Profil" role="group">
            <Link to="/profil" onClick={onClose} className="flex min-h-11 min-w-0 flex-1 items-center gap-[10px] rounded-full py-1 pl-[2px]">
              <Avatar initials={me.initials} />
              <div className="min-w-0">
                <b className="block truncate text-md font-normal">{me.displayName}</b>
                <span className="block text-caption whitespace-nowrap text-ink-3">{me.company}</span>
              </div>
            </Link>
            <button
              type="button"
              onClick={() => {
                // Schublade schließen, damit die Leiste „Nicht gespeichert“ im Datensatz sichtbar ist.
                onClose();
                attemptLeave(() => void auth.signOut());
              }}
              className="inline-flex h-11 flex-none cursor-pointer items-center gap-2 rounded-full px-3 text-row text-ink-2"
            >
              <LogOut size={16} strokeWidth={1.5} aria-hidden />
              Abmelden
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
